import {
  Button,
  GreyGrey80,
  Primary13Regular,
  SequenceMenu,
  Tooltip,
  SimpleModal,
  Box
} from '@sequencehq/core-components'
import { useAvailableFeatures, useMutators, useStatus } from 'Cube/domain'
import { CubeStatus, DisabledReasonType } from 'Cube/domain/cube.domain.types'
import { match } from 'ts-pattern'
import { AMENDMENT_OUT_FOR_SIGNATURE_REASON } from 'Cube/view/layouts/quote/AmendmentOutForSignatureBanner/AmendmentOutForSignatureBanner.constants'

const AcceptQuoteModal = (props: {
  trigger: React.ReactElement
  onSubmit?: () => void
}) => {
  const { status } = useStatus()
  const { mutators } = useMutators()
  const warningCopy = match(status)
    .with(
      CubeStatus.QuoteReadyToSign,
      () =>
        "You're about to mark this quote as signed and cancel all pending signature requests. This quote will be locked and no further changes can be made."
    )
    .otherwise(
      () =>
        "You're about to mark this quote as signed. This quote will be locked and no further changes can be made."
    )

  return (
    <SimpleModal
      data-testid="acceptQuoteModal"
      variant="dialog"
      trigger={props.trigger}
      onSubmit={async () => {
        await mutators.external.out.quote?.accept()
        props.onSubmit?.()
        return true
      }}
      title="Mark as signed"
      submitButtonText="Mark as signed"
      submittingButtonText="Marking as signed..."
    >
      <Box {...Primary13Regular} color={GreyGrey80}>
        {warningCopy}
      </Box>
    </SimpleModal>
  )
}

const useAcceptQuote = () => {
  const { availableFeatures } = useAvailableFeatures()
  const { available, reasons } = availableFeatures.quote.accept

  return {
    disabled: !available.enabled,
    disabledReason: reasons.length
      ? match(reasons[0].reasonType)
          .with(
            DisabledReasonType.AmendmentOutForSignature,
            () => AMENDMENT_OUT_FOR_SIGNATURE_REASON
          )
          .with(
            DisabledReasonType.CantChangeWhenExpired,
            () => `Can't mark an expired quote as signed`
          )
          .otherwise(() => `Can't mark this quote as signed`)
      : undefined
  }
}

export const AcceptQuoteButton = () => {
  const { disabled, disabledReason } = useAcceptQuote()

  return (
    <AcceptQuoteModal
      trigger={
        <Tooltip isDisabled={!disabledReason} label={disabledReason}>
          <Button
            variant="primary"
            disabled={disabled}
            data-testid="quote.accept"
          >
            Mark as signed
          </Button>
        </Tooltip>
      }
    />
  )
}

export const AcceptQuoteMenuItem = (props: { onSubmit?: () => void }) => {
  const { disabled, disabledReason } = useAcceptQuote()

  return (
    <AcceptQuoteModal
      onSubmit={props.onSubmit}
      trigger={
        <SequenceMenu.Item
          uuid="accept-quote"
          label="Mark as signed"
          isDisabled={disabled}
          tooltip={disabled ? disabledReason : undefined}
          data-testid="quotes.accept.menuItem"
        />
      }
    />
  )
}
